import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Spinner,
  Box,
  Link,
} from "@chakra-ui/react";
import { useGame } from "../../contexts/GameContext";
import { useState } from "react";
import { lowerCase, toLower } from "lodash";
import { useParams, Link as RouterLink } from "react-router-dom";

export const SignIn: React.FC = () => {
  const { gameId: urlGameId } = useParams<{ gameId: string }>();
  const [playerName, setPlayerName] = useState("" as string);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [gameId, setGameIdInput] = useState(urlGameId || "");
  const [customTopic, setCustomTopic] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const { gameState, addPlayer, isLoadingGameState } = useGame();

  // Check if game is full (shouldn't happen due to PlayerGate, but keep as fallback)
  const isGameFull = gameState?.maxPlayers != null &&
    gameState.players.length >= gameState.maxPlayers;

  const onAddPlayer = async () => {
    if (!playerName.trim() || !gameId.trim()) {
      setHasError(true);
      setErrorMessage("Please enter your name and game code");
      return;
    }

    setIsJoining(true);
    setHasError(false);
    setErrorMessage("");

    try {
      console.log('[SignIn] Adding player:', {
        name: playerName.trim(),
        gameId,
        customTopic: customTopic.trim() || undefined,
      });
      await addPlayer({
        name: playerName.trim(),
        gameId,
        customTopic: customTopic.trim() || undefined,
      });
      console.log('[SignIn] Player added successfully');
      // Note: Don't set isJoining to false here - the component will unmount
      // as currentPlayer gets set and we transition to the game view
    } catch (e: any) {
      console.error('[SignIn] Error adding player:', e);
      setIsJoining(false);
      setHasError(true);
      // Try to extract a useful error message
      if (e?.code === 'resource-exhausted' || e?.message?.includes('full') || e?.message?.includes('Maximum')) {
        setErrorMessage("This game is full. Try joining as an audience member instead.");
      } else if (e?.code === 'already-exists' || e?.message?.includes('already exists')) {
        setErrorMessage("This name is already taken. Please choose another.");
      } else if (e?.message?.includes('not-found') || e?.message?.includes('No game')) {
        setErrorMessage("Game not found. Please check the game code.");
      } else {
        setErrorMessage("Failed to join game. Please try again.");
      }
    }
  };

  const playerNameUnique = gameState?.players
    ? gameState?.players.every(
      (player) => lowerCase(player.name) !== lowerCase(playerName)
    )
    : true;

  const isNameInvalid = (hasError && errorMessage.includes("name")) || !playerNameUnique;
  const canJoin = playerName.trim() && gameId.trim() && playerNameUnique && !isJoining && !isGameFull;

  return (
    <Flex
      p="4em"
      height="100%"
      flexDir="column"
      gap="19px"
      justifyContent="center"
    >
      <Text fontSize="4em" flexGrow={1} textStyle="jeoparty">
        JEOPARTY
      </Text>
      <FormControl>
        <FormLabel textStyle="clue">Player Name</FormLabel>
        <Input
          isInvalid={isNameInvalid}
          maxLength={10}
          value={playerName}
          backgroundColor="white"
          onChange={(e) => setPlayerName(e.target.value)}
        />
        {isNameInvalid && (
          <Text textStyle="clue" color="red" fontSize="10px" marginTop="6px">
            {!playerNameUnique ? "Player name already taken" : "Please enter a valid name"}
          </Text>
        )}
      </FormControl>
      <FormControl>
        <FormLabel textStyle="clueActive">Game Code</FormLabel>
        <Input
          disabled={!!urlGameId}
          maxLength={7}
          value={gameId}
          backgroundColor="white"
          onChange={(e) => setGameIdInput(toLower(e.target.value))}
        />
      </FormControl>
      <FormControl>
        <FormLabel textStyle="clue">Submit a Category (Optional)</FormLabel>
        <Input
          value={customTopic}
          onChange={(e) => setCustomTopic(e.target.value)}
          backgroundColor="white"
          placeholder="e.g., 90s Hip Hop, Marine Biology"
          maxLength={50}
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Vote on categories in the lobby - top picks go to Double Jeopardy!
        </Text>
      </FormControl>
      {gameState && (
        <Text textStyle="clue">
          {gameState.maxPlayers != null
            ? `${gameState.players.length}/${gameState.maxPlayers} players`
            : `${gameState.players.length} player${gameState.players.length !== 1 ? 's' : ''}`
          }
        </Text>
      )}
      {isGameFull && (
        <Box bg="orange.500" p={3} borderRadius="md" width="100%">
          <Text textStyle="clue" fontSize="sm" textAlign="center" mb={2}>
            This game is full ({gameState?.maxPlayers} players max)
          </Text>
          <Link
            as={RouterLink}
            to={`/audience/${gameId}`}
            color="white"
            textDecoration="underline"
            display="block"
            textAlign="center"
          >
            <Text textStyle="clue" fontSize="sm">
              Join as audience instead
            </Text>
          </Link>
        </Box>
      )}
      {isLoadingGameState && gameId && (
        <Flex alignItems="center" gap={2}>
          <Spinner size="sm" color="white" />
          <Text textStyle="clue" fontSize="sm">Loading game...</Text>
        </Flex>
      )}
      {hasError && errorMessage && (
        <Box bg="red.500" p={2} borderRadius="md" width="100%">
          <Text textStyle="clue" fontSize="sm" textAlign="center">
            {errorMessage}
          </Text>
        </Box>
      )}
      <Button
        background="jeopardy.lightBlue"
        onClick={onAddPlayer}
        isDisabled={!canJoin}
        isLoading={isJoining}
        loadingText="Joining..."
        width="100%"
      >
        <Text textStyle="clue">Join</Text>
      </Button>
    </Flex>
  );
};
